import axios from 'axios';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { aggregateOrderbook } from '../utils';

const CRYPTO_COM_REST_BASE = 'https://api.crypto.com/exchange/v1';
const CRYPTO_COM_WS_URL = 'wss://stream.crypto.com/exchange/v1/market';
const CRYPTO_COM_MAX_CANDLES = 300;
const CRYPTO_COM_REST_BOOK_DEPTH = 20;
const CRYPTO_COM_WS_BOOK_DEPTH = 10;
const CRYPTO_COM_SUBSCRIBE_DELAY_MS = 1000;

const CRYPTO_COM_TIMEFRAMES: Partial<
  Record<IChartTimeframe, { name: string; durationMs: number }>
> = {
  '1m': { name: '1m', durationMs: 60_000 },
  '5m': { name: '5m', durationMs: 300_000 },
  '15m': { name: '15m', durationMs: 900_000 },
  '30m': { name: '30m', durationMs: 1_800_000 },
  '1h': { name: '1h', durationMs: 3_600_000 },
  '4h': { name: '4h', durationMs: 14_400_000 },
  '24h': { name: '1D', durationMs: 86_400_000 },
  '1d': { name: '1D', durationMs: 86_400_000 },
  '1w': { name: '7D', durationMs: 604_800_000 },
  '1M': { name: '1M', durationMs: 2_592_000_000 },
};

interface CryptoComEnvelope<T> {
  code: number;
  result?: T;
  message?: string;
}

interface CryptoComInstrument {
  symbol: string;
  price_tick_size: string;
  qty_tick_size: string;
}

interface CryptoComTicker {
  i: string;
  a: string;
  h: string;
  l: string;
  c: string;
  v: string;
  vv: string;
  b?: string;
  bs?: string;
  k?: string;
  ks?: string;
  t: number;
}

type CryptoComBookLevel = [price: string, size: string, ...metadata: string[]];

interface CryptoComBookSnapshot {
  bids: CryptoComBookLevel[];
  asks: CryptoComBookLevel[];
  t?: number;
  u?: number;
}

export interface CryptoComCandle {
  t: number;
  o: string;
  h: string;
  l: string;
  c: string;
  v: string;
}

interface CryptoComTrade {
  i: string;
  d: string;
  s: string;
  p: string;
  q: string;
  t: number;
}

interface CryptoComWsResult {
  instrument_name?: string;
  subscription?: string;
  channel?: 'book' | 'ticker' | 'trade';
  data?: unknown[];
}

interface CryptoComWsMessage {
  id?: number;
  method: string;
  code?: number;
  message?: string;
  result?: CryptoComWsResult;
}

export interface CryptoComOrderbookState {
  bids: Map<string, string>;
  asks: Map<string, string>;
}

export const buildCryptoComInstrumentName = (base: string, quote: string) =>
  `${base.toUpperCase()}_${quote.toUpperCase()}`;

export const normalizeCryptoComCandles = (candles: CryptoComCandle[]): KLine[] =>
  candles
    .map(
      candle =>
        [
          Number(candle.t),
          String(candle.o),
          String(candle.h),
          String(candle.l),
          String(candle.c),
          String(candle.v),
        ] as KLine,
    )
    .sort((left, right) => left[0] - right[0]);

export const cryptoComPrecisionFromTick = (tick: string | undefined): number => {
  if (!tick || !Number.isFinite(Number(tick)) || Number(tick) <= 0) {
    return 1;
  }

  const [, decimals = ''] = tick.split('.');
  return decimals.replace(/0+$/, '').length;
};

export const createCryptoComOrderbookState = (): CryptoComOrderbookState => ({
  bids: new Map(),
  asks: new Map(),
});

export const applyCryptoComOrderbookSnapshot = (
  state: CryptoComOrderbookState,
  snapshot: Pick<CryptoComBookSnapshot, 'bids' | 'asks'>,
) => {
  state.bids.clear();
  state.asks.clear();
  snapshot.bids.forEach(([price, size]) => state.bids.set(price, size));
  snapshot.asks.forEach(([price, size]) => state.asks.set(price, size));
};

export const buildCryptoComSubscription = (instrumentName: string, nonce: number) => ({
  id: 1,
  method: 'subscribe' as const,
  params: {
    channels: [
      `ticker.${instrumentName}`,
      `book.${instrumentName}.${CRYPTO_COM_WS_BOOK_DEPTH}`,
      `trade.${instrumentName}`,
    ],
  },
  nonce,
});

const readCryptoComResult = <T>(envelope: CryptoComEnvelope<T>, method: string): T => {
  if (envelope.code !== 0 || !envelope.result) {
    throw new Error(`Crypto.com ${method}: ${envelope.message ?? `code ${envelope.code}`}`);
  }
  return envelope.result;
};

export class CryptoComStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pair: string;
  displayDepth: number;
  precision = 1;
  selectedPrecision = 1;
  orderbook = createCryptoComOrderbookState();
  subscribeTimer: ReturnType<typeof setTimeout> | undefined;
  liveIntervalMs = 60_000;
  currentKline: KLine | undefined;

  onOrderbookUpdate: (value: IOrderbook) => void;
  onTickerUpdate: (value: ITickerSocket) => void;
  onTradeUpdate: (value: TradeSocket) => void;
  klineUpdate: (value: KLine) => void;
  precisionUpdate: (value: number) => void;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    onTradeUpdate,
    klineUpdate,
    precisionUpdate,
  }: IInitArgs) {
    this.pair = buildCryptoComInstrumentName(base, quote);
    this.displayDepth = orderbookDepth ?? CRYPTO_COM_REST_BOOK_DEPTH;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.onTradeUpdate = onTradeUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
  }

  publishOrderbook = () => {
    const asks = [...this.orderbook.asks.entries()].sort(
      (left, right) => Number(left[0]) - Number(right[0]),
    );
    const bids = [...this.orderbook.bids.entries()].sort(
      (left, right) => Number(right[0]) - Number(left[0]),
    );

    this.onOrderbookUpdate(
      aggregateOrderbook(asks, bids, this.selectedPrecision, this.displayDepth),
    );
  };

  publishTicker = (ticker: CryptoComTicker) => {
    const lastNumber = Number(ticker.a);
    const rawChange = Number(ticker.c);
    this.onTickerUpdate({
      baseVolume: ticker.v,
      change: normalizeNumber(Number.isFinite(rawChange) ? rawChange * 100 : 0),
      close: ticker.a,
      high: ticker.h,
      last: Number.isFinite(lastNumber) ? lastNumber.toFixed(this.precision) : ticker.a,
      low: ticker.l,
      quoteVolume: ticker.vv,
    });
  };

  fetchInstrument = async () => {
    try {
      const response = await axios.get<CryptoComEnvelope<{ data: CryptoComInstrument[] }>>(
        `${CRYPTO_COM_REST_BASE}/public/get-instruments`,
      );
      const instruments = readCryptoComResult(response.data, 'public/get-instruments').data;
      const instrument = instruments.find(item => item.symbol === this.pair);
      if (!instrument) {
        throw new Error(`Crypto.com instrument ${this.pair} not found`);
      }
      const precision = cryptoComPrecisionFromTick(instrument.price_tick_size);
      this.precision = precision;
      this.selectedPrecision = precision;
      this.precisionUpdate(precision);
      if (this.orderbook.bids.size || this.orderbook.asks.size) {
        this.publishOrderbook();
      }
    } catch (error) {
      console.log('Crypto.com instrument error:', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const response = await axios.get<CryptoComEnvelope<{ data: CryptoComBookSnapshot[] }>>(
        `${CRYPTO_COM_REST_BASE}/public/get-book`,
        {
          params: {
            instrument_name: this.pair,
            depth: CRYPTO_COM_REST_BOOK_DEPTH,
          },
        },
      );
      const snapshot = readCryptoComResult(response.data, 'public/get-book').data[0];
      if (!snapshot) throw new Error(`Crypto.com orderbook ${this.pair} is empty`);

      applyCryptoComOrderbookSnapshot(this.orderbook, snapshot);
      this.publishOrderbook();
    } catch (error) {
      console.log('Crypto.com orderbook error:', error);
    }
  };

  fetchTicker = async () => {
    try {
      const response = await axios.get<CryptoComEnvelope<{ data: CryptoComTicker[] }>>(
        `${CRYPTO_COM_REST_BASE}/public/get-tickers`,
        { params: { instrument_name: this.pair } },
      );
      const ticker = readCryptoComResult(response.data, 'public/get-tickers').data.find(
        item => item.i === this.pair,
      );
      if (!ticker) throw new Error(`Crypto.com ticker ${this.pair} is empty`);
      this.publishTicker(ticker);
    } catch (error) {
      console.log('Crypto.com ticker error:', error);
    }
  };

  fetchKlines = async ({
    from,
    to,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }): Promise<KLine[]> => {
    const timeframe = CRYPTO_COM_TIMEFRAMES[interval];
    if (!timeframe) return [];

    const boundedLimit = Math.min(CRYPTO_COM_MAX_CANDLES, Math.max(1, limit));
    const endMs = to > 0 ? to : Date.now();
    const earliestMs = endMs - timeframe.durationMs * boundedLimit;
    const startMs = from > 0 ? Math.max(from, earliestMs) : earliestMs;

    try {
      const response = await axios.get<CryptoComEnvelope<{ data: CryptoComCandle[] }>>(
        `${CRYPTO_COM_REST_BASE}/public/get-candlestick`,
        {
          params: {
            instrument_name: this.pair,
            timeframe: timeframe.name,
            count: boundedLimit,
            start_ts: startMs,
            end_ts: endMs,
          },
        },
      );
      const candles = readCryptoComResult(response.data, 'public/get-candlestick').data;
      return normalizeCryptoComCandles(candles).slice(-boundedLimit);
    } catch (error) {
      console.log('Crypto.com klines error:', error);
      return [];
    }
  };

  fetchTrades = async ({ limit }: { base: string; quote: string; limit: number }) => {
    try {
      const response = await axios.get<CryptoComEnvelope<{ data: CryptoComTrade[] }>>(
        `${CRYPTO_COM_REST_BASE}/public/get-trades`,
        {
          params: {
            instrument_name: this.pair,
            count: Math.min(50, Math.max(1, limit)),
          },
        },
      );
      return readCryptoComResult(response.data, 'public/get-trades').data.map(trade => ({
        price: trade.p,
        quantity: trade.q,
        timestamp: String(trade.t),
        side: trade.s.toLowerCase(),
      }));
    } catch (error) {
      console.log('Crypto.com trades error:', error);
      return [];
    }
  };

  updateKlineFromTrade = (trade: CryptoComTrade) => {
    const timestampMs = Number(trade.t);
    const priceNumber = Number(trade.p);
    const sizeNumber = Number(trade.q);
    if (
      !Number.isFinite(timestampMs) ||
      !Number.isFinite(priceNumber) ||
      !Number.isFinite(sizeNumber)
    ) {
      return;
    }

    const bucketSeconds =
      Math.floor(timestampMs / this.liveIntervalMs) * (this.liveIntervalMs / 1000);
    if (!this.currentKline || this.currentKline[0] !== bucketSeconds) {
      this.currentKline = [bucketSeconds, trade.p, trade.p, trade.p, trade.p, trade.q];
    } else {
      this.currentKline = [
        bucketSeconds,
        this.currentKline[1],
        String(Math.max(Number(this.currentKline[2]), priceNumber)),
        String(Math.min(Number(this.currentKline[3]), priceNumber)),
        trade.p,
        normalizeNumber(Number(this.currentKline[5]) + sizeNumber),
      ];
    }
    this.klineUpdate(this.currentKline);
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();
    this.liveIntervalMs = interval > 0 ? interval : 60_000;

    const ws = new ReconnectingWebSocket(CRYPTO_COM_WS_URL, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });
    this.ws = ws;

    ws.onopen = () => {
      this.orderbook.bids.clear();
      this.orderbook.asks.clear();
      this.currentKline = undefined;
      if (this.subscribeTimer) clearTimeout(this.subscribeTimer);
      this.subscribeTimer = setTimeout(() => {
        ws.send(JSON.stringify(buildCryptoComSubscription(this.pair, Date.now())));
        this.subscribeTimer = undefined;
      }, CRYPTO_COM_SUBSCRIBE_DELAY_MS);
    };

    ws.onmessage = event => {
      try {
        if (typeof event.data !== 'string') return;
        const message = JSON.parse(event.data) as CryptoComWsMessage;

        if (message.method === 'public/heartbeat' && message.id !== undefined) {
          ws.send(
            JSON.stringify({
              id: message.id,
              method: 'public/respond-heartbeat',
            }),
          );
          return;
        }
        if (message.code && message.code !== 0) {
          console.log('Crypto.com websocket subscription error:', message.code, message.message);
          return;
        }

        const result = message.result;
        if (!result?.channel || !result.data) return;
        if (result.channel === 'book') {
          const snapshot = result.data[0] as CryptoComBookSnapshot | undefined;
          if (!snapshot?.bids || !snapshot.asks) return;
          applyCryptoComOrderbookSnapshot(this.orderbook, snapshot);
          this.publishOrderbook();
          return;
        }
        if (result.channel === 'ticker') {
          const ticker = result.data[0] as CryptoComTicker | undefined;
          if (ticker) this.publishTicker(ticker);
          return;
        }
        if (result.channel === 'trade') {
          const trades = [...(result.data as CryptoComTrade[])].sort(
            (left, right) => left.t - right.t,
          );
          trades.forEach(trade => {
            this.onTradeUpdate({
              price: trade.p,
              quantity: trade.q,
              timestamp: String(trade.t),
              side: trade.s.toLowerCase(),
            });
            this.updateKlineFromTrade(trade);
          });
        }
      } catch (error) {
        console.log('Crypto.com websocket error:', error);
      }
    };
  };

  closeWebSocket = () => {
    this.ws?.close();
    this.ws = undefined;
    if (this.subscribeTimer) {
      clearTimeout(this.subscribeTimer);
      this.subscribeTimer = undefined;
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;
    this.publishOrderbook();
  };

  setOrderbookDepth = (depth: number) => {
    this.displayDepth = depth;
    this.publishOrderbook();
  };

  getOrderbookTicks = (precision: number) =>
    Array.from({ length: precision })
      .fill(0)
      .map((_, index) => index + 1)
      .map(value => ({
        value: normalizeNumber(1 / 10 ** value),
        tick: value,
        id: value,
      }))
      .slice(-3);

  fetch = async () => {
    await Promise.all([this.fetchInstrument(), this.fetchOrderbook(), this.fetchTicker()]);
  };

  static init = async (args: IInitArgs) => {
    const strategy = new CryptoComStrategy(args);
    await strategy.fetch();
    return strategy;
  };
}
